import { useState, useEffect } from "react";
import {
  Plus,
  Edit2,
  Trash2,
  Save,
  X,
  Download,
  User,
  Calendar,
  FileStack,
  CheckSquare,
  Search,
  Filter,
  ChevronLeft,
  ChevronRight,
  ChevronsLeft,
  ChevronsRight,
  MessageSquare,
  Eye,
} from "lucide-react";
import {
  folhaPagamentoService,
  profissionalService,
} from "../../services/apiService";
import { useNotification } from "../../hooks/useNotification";
import { useConfirmDialog } from "../../hooks/useConfirmDialog";
import PageContainer from "../../components/PageContainer";
import { cardClass } from "../../components/ui";
import { formatarData } from "../../utils/FormatarData";
import {
  formatarValorReal,
  formatarPorcentagem,
  formatCurrencyMoeda,
} from "../../utils/FormatarValores";

function obterCompetenciaAtual() {
  const hoje = new Date();
  const ano = hoje.getFullYear();
  const mes = String(hoje.getMonth() + 1).padStart(2, "0");
  return `${ano}-${mes}`;
}

const FolhaPagamento = () => {
  const [folhasPagamento, setFolhasPagamento] = useState([]);
  const [folhasFiltradas, setFolhasFiltradas] = useState([]);
  const [profissionais, setProfissionais] = useState([]);
  const [especialidadesProfissional, setEspecialidadesProfissional] = useState(
    []
  );
  const [loading, setLoading] = useState(false);
  const [showForm, setShowForm] = useState(false);
  const [editingItem, setEditingItem] = useState(null);
  const [selectedItems, setSelectedItems] = useState([]);
  const [selectionMode, setSelectionMode] = useState(false);
  const [showObservacao, setShowObservacao] = useState(false);
  const [observacaoView, setObservacaoView] = useState("");

  // Estados para filtros
  const [filtros, setFiltros] = useState({
    tipoPesquisa: "todas",
    profissionalId: "",
    dataInicio: "",
    dataFinal: "",
  });

  // Estados para paginação
  const [paginacao, setPaginacao] = useState({
    paginaAtual: 1,
    itensPorPagina: 10,
  });

  const [formData, setFormData] = useState({
    profissional: { id: "", nome: "" },
    competenciaPrestacaoServico: obterCompetenciaAtual(),
    dataInicialPrestacaoServico: "",
    dataFinalPrestacaoServico: "",
    especialidadeBonus: [],
    valorAcrescimo: 0,
    valorDesconto: 0,
    valorLiquido: 0,
    pagamentoRecebido: 0,
    observacao: "",
  });
  const [displayValues, setDisplayValues] = useState({
    valorAcrescimo: "",
    valorDesconto: "",
    pagamentoRecebido: "",
  });
  const [displayValuesEspec, setDisplayValuesEspec] = useState({});
  const { showError, showSuccess } = useNotification();
  const confirmDialog = useConfirmDialog();

  // Carregar dados iniciais
  // eslint-disable-next-line react-hooks/exhaustive-deps
  useEffect(() => {
    loadFolhasPagamento();
    loadProfissionais();
  }, [filtros]); // eslint-disable-line react-hooks/exhaustive-deps

  // Aplicar filtros quando mudarem
  // eslint-disable-next-line react-hooks/exhaustive-deps
  useEffect(() => {
    aplicarFiltros();
    setPaginacao((prev) => ({ ...prev, paginaAtual: 1 })); // Reset para primeira página ao filtrar
  }, [folhasPagamento, filtros]); // eslint-disable-line react-hooks/exhaustive-deps

  // Reset página quando aplicar novos filtros
  useEffect(() => {
    setPaginacao((prev) => ({ ...prev, paginaAtual: 1 }));
  }, [filtros]);

  const loadFolhasPagamento = async () => {
    setLoading(true);
    try {
      let data;

      switch (filtros.tipoPesquisa) {
        case "profissional":
          if (filtros.profissionalId.trim()) {
            data = await folhaPagamentoService.listarFolhaPagamentos(
              filtros.profissionalId
            );
          } else {
            data = await folhaPagamentoService.listarFolhaPagamentos();
          }
          break;

        case "periodo":
          if (filtros.dataInicio && filtros.dataFinal) {
            data = await folhaPagamentoService.listarFolhaPagamentoPorPeriodo(
              filtros.dataInicio,
              filtros.dataFinal
            );
          } else {
            data = await folhaPagamentoService.listarFolhaPagamentos();
          }
          break;

        case "profissional_periodo":
          if (
            filtros.profissionalId &&
            filtros.dataInicio &&
            filtros.dataFinal
          ) {
            data =
              await folhaPagamentoService.listarFolhaPagamentoPorProfissionalIdEoPeriodo(
                filtros.profissionalId,
                filtros.dataInicio,
                filtros.dataFinal
              );
          } else {
            data = await folhaPagamentoService.listarFolhaPagamentos();
          }
          break;

        default:
          data = await folhaPagamentoService.listarFolhaPagamentos();
          break;
      }

      setFolhasPagamento(data);
    } catch (err) {
    } finally {
      setLoading(false);
    }
  };

  const aplicarFiltros = () => {
    setFolhasFiltradas([...folhasPagamento]);
  };

  const handleFiltroChange = (e) => {
    const { name, value } = e.target;
    setFiltros((prev) => ({
      ...prev,
      [name]: value,
    }));
  };

  const aplicarFiltrosBusca = () => {
    // Validação de datas
    if (filtros.dataInicio && filtros.dataFinal) {
      const dataInicio = new Date(filtros.dataInicio);
      const dataFinal = new Date(filtros.dataFinal);

      if (dataInicio > dataFinal) {
        showError("A data inicial não pode ser maior que a data final");
        return;
      }
    }

    // Validação para tipo profissional
    if (filtros.tipoPesquisa === "profissional" && !filtros.profissionalId) {
      showError("Selecione um profissional para busca");
      return;
    }

    // Validação para tipo período
    if (filtros.tipoPesquisa === "periodo") {
      if (!filtros.dataInicio || !filtros.dataFinal) {
        showError("Selecione as datas inicial e final para busca por período");
        return;
      }
    }

    // Validação para tipo profissional + período
    if (filtros.tipoPesquisa === "profissional_periodo") {
      if (!filtros.profissionalId) {
        showError("Selecione um profissional");
        return;
      }
      if (!filtros.dataInicio || !filtros.dataFinal) {
        showError("Selecione as datas inicial e final");
        return;
      }
    }

    // Se passou na validação, carrega os dados
    loadFolhasPagamento();
  };

  const limparFiltros = () => {
    setFiltros({
      tipoPesquisa: "todas",
      profissionalId: "",
      dataInicio: "",
      dataFinal: "",
    });
    setPaginacao((prev) => ({ ...prev, paginaAtual: 1 }));
  };

  const isDatasValidas = () => {
    if (filtros.dataInicio && filtros.dataFinal) {
      const dataInicio = new Date(filtros.dataInicio);
      const dataFinal = new Date(filtros.dataFinal);
      return dataInicio <= dataFinal;
    }
    return true; // Se não tem as duas datas, é válido
  };

  // Função para mostrar observação
  const mostrarObservacao = (observacao) => {
    setObservacaoView(observacao || "Nenhuma observação");
    setShowObservacao(true);
  };

  // Cálculos de paginação
  const totalRegistros = folhasFiltradas.length;
  const totalPaginas = Math.ceil(totalRegistros / paginacao.itensPorPagina);
  const indiceInicial = (paginacao.paginaAtual - 1) * paginacao.itensPorPagina;
  const indiceFinal = indiceInicial + paginacao.itensPorPagina;
  const folhasPaginadas = folhasFiltradas.slice(indiceInicial, indiceFinal);

  // Funções de navegação
  const irParaPrimeiraPagina = () => {
    setPaginacao((prev) => ({ ...prev, paginaAtual: 1 }));
  };

  const irParaUltimaPagina = () => {
    setPaginacao((prev) => ({ ...prev, paginaAtual: totalPaginas }));
  };

  const irParaPaginaAnterior = () => {
    setPaginacao((prev) => ({
      ...prev,
      paginaAtual: Math.max(1, prev.paginaAtual - 1),
    }));
  };

  const irParaProximaPagina = () => {
    setPaginacao((prev) => ({
      ...prev,
      paginaAtual: Math.min(totalPaginas, prev.paginaAtual + 1),
    }));
  };

  const irParaPagina = (numeroPagina) => {
    setPaginacao((prev) => ({ ...prev, paginaAtual: numeroPagina }));
  };

  // Gerar números das páginas para exibição
  const gerarNumerosPaginas = () => {
    const paginas = [];
    const maxPaginasVisiveis = 5;
    let inicio = Math.max(
      1,
      paginacao.paginaAtual - Math.floor(maxPaginasVisiveis / 2)
    );
    let fim = Math.min(totalPaginas, inicio + maxPaginasVisiveis - 1);

    // Ajustar início se estivermos no final
    if (fim - inicio < maxPaginasVisiveis - 1) {
      inicio = Math.max(1, fim - maxPaginasVisiveis + 1);
    }

    for (let i = inicio; i <= fim; i++) {
      paginas.push(i);
    }

    return paginas;
  };

  const loadProfissionais = async () => {
    try {
      const data = await profissionalService.listarProfissionais();
      setProfissionais(data.filter((p) => p.ativo));
    } catch (err) {}
  };

  const toggleSelectionMode = () => {
    setSelectionMode(!selectionMode);
    setSelectedItems([]);
  };

  const handleSelectItem = (id) => {
    setSelectedItems((prev) =>
      prev.includes(id) ? prev.filter((item) => item !== id) : [...prev, id]
    );
  };

  const handleSelectAll = () => {
    if (selectedItems.length === folhasPaginadas.length) {
      setSelectedItems([]);
    } else {
      setSelectedItems(folhasPaginadas.map((item) => item.id));
    }
  };

  const gerarPDFLote = async () => {
    if (selectedItems.length === 0) {
      showError("Selecione pelo menos uma folha de pagamento");
      return;
    }

    setLoading(true);
    try {
      const pdfBlob = await folhaPagamentoService.gerarVariosPdfFolhaPagamento(
        selectedItems
      );

      const url = window.URL.createObjectURL(pdfBlob);
      const link = document.createElement("a");
      link.href = url;
      link.download = `folhas_pagamento_lote.pdf`;
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);
      window.URL.revokeObjectURL(url);

      showSuccess(
        `PDF gerado com sucesso para ${selectedItems.length} folhas!`
      );
      setSelectedItems([]);
    } catch (err) {
      showError(err.message || "Erro ao gerar PDF em lote");
    } finally {
      setLoading(false);
    }
  };

  const handleChange = (e) => {
    const { name, value } = e.target;

    if (name === "valorAcrescimo" || name === "valorDesconto" || name === "pagamentoRecebido") {
      const { formatted, numeric } = formatarValorReal(value);

      setFormData((prev) => ({
        ...prev,
        [name]: numeric,
      }));

      setDisplayValues((prev) => ({
        ...prev,
        [name]: formatted,
      }));
    } else if (name === "competenciaPrestacaoServico") {
      const periodo = obterPeriodoDaCompetencia(value);

      setFormData((prev) => ({
        ...prev,
        competenciaPrestacaoServico: value,
        ...periodo,
      }));
    } else {
      setFormData((prev) => ({
        ...prev,
        [name]: value,
      }));
    }
  };

  const handleProfissionalChange = async (e) => {
    const profissionalId = e.target.value;
    const profissional = profissionais.find((p) => p.id === profissionalId) || {
      id: "",
      nome: "",
    };

    setFormData((prev) => ({
      ...prev,
      profissional: profissional,
      especialidadeBonus: [],
    }));

    setDisplayValuesEspec({});

    if (profissionalId) {
      try {
        const profissionalCompleto =
          await profissionalService.listarProfissionalPorId(profissionalId);
        const especialidades = profissionalCompleto.especialidade || [];
        setEspecialidadesProfissional(especialidades);

        const especialidadesBonus = especialidades.map((esp) => ({
          especialidade: {
            id: esp.id,
            nome: esp.nome,
            valorPercentual: esp.valorPercentual || 0,
          },
          valorBase: 0,
          valorBonus: 0,
        }));

        setFormData((prev) => ({
          ...prev,
          especialidadeBonus: especialidadesBonus,
        }));

        // Inicializar valores de display para especialidades
        const initialDisplayValues = {};
        especialidadesBonus.forEach((_, index) => {
          initialDisplayValues[`${index}_valorBase`] = "";
          initialDisplayValues[`${index}_valorBonus`] = "";
        });
        setDisplayValuesEspec(initialDisplayValues);
      } catch (err) {
        setEspecialidadesProfissional([]);
      }
    } else {
      setEspecialidadesProfissional([]);
    }
  };

  const calcularValorEspecialidade = (item) => {
    const valorBase = parseFloat(item.valorBase) || 0;
    const valorBonus = parseFloat(item.valorBonus) || 0;
    const valorPercentual =
      parseFloat(item.especialidade?.valorPercentual) || 0;
    
    if (valorPercentual === 0 && valorBonus === 0) {
      return valorBase;
    }
    else{
      return valorBase * ((valorPercentual + valorBonus) / 100);
    }
  };

  const calcularValorLiquido = () => {
    const totalEspecialidades = formData.especialidadeBonus.reduce(
      (sum, item) => {
        const valor = calcularValorEspecialidade(item);
        return sum + (isNaN(valor) ? 0 : valor);
      },
      0
    );

    const acrescimo = parseFloat(formData.valorAcrescimo) || 0;
    const desconto = parseFloat(formData.valorDesconto) || 0;
    const liquido = totalEspecialidades + acrescimo - desconto;

    setFormData((prev) => ({
      ...prev,
      valorLiquido: isNaN(liquido) ? 0 : liquido,
    }));
  };


  const calcularValorTotalEspecialidade = (item) => {
    const valorBase = item.valorBase || 0;
    const valorBonus = item.valorBonus || 0;
    const valorPercentual = item.especialidade?.valorPercentual || 0;
    
    if (valorPercentual === 0 && valorBonus === 0) {
      return valorBase;
    }
    
    return valorBase * ((valorPercentual + valorBonus) / 100);
  };

  // eslint-disable-next-line react-hooks/exhaustive-deps
  useEffect(() => {
    calcularValorLiquido();
  }, [ // eslint-disable-line react-hooks/exhaustive-deps
    formData.especialidadeBonus,
    formData.valorAcrescimo,
    formData.valorDesconto,
  ]);

  const updateEspecialidadeBonus = (index, field, value) => {
    const key = `${index}_${field}`;

    if (field === "valorBase") {
      const { formatted, numeric } = formatarValorReal(value);

      setFormData((prev) => ({
        ...prev,
        especialidadeBonus: prev.especialidadeBonus.map((item, i) =>
          i === index ? { ...item, [field]: numeric } : item
        ),
      }));

      setDisplayValuesEspec((prev) => ({
        ...prev,
        [key]: formatted,
      }));
    } else if (field === "valorBonus") {
      const { formatted, numeric } = formatarPorcentagem(value);

      setFormData((prev) => ({
        ...prev,
        especialidadeBonus: prev.especialidadeBonus.map((item, i) =>
          i === index ? { ...item, [field]: numeric } : item
        ),
      }));

      setDisplayValuesEspec((prev) => ({
        ...prev,
        [key]: formatted,
      }));
    }
  };

  const formatarValorMoedaParaInput = (valor) => {
    if (valor === null || valor === undefined || valor === "") {
      return formatarValorReal(0).formatted;
    }

    const valorTexto = String(valor);
    const valorParaMascara =
      valorTexto.includes(",") || valorTexto.includes(".")
        ? valorTexto
        : (Number(valor) * 100).toString();

    return formatarValorReal(valorParaMascara).formatted;
  };

  const formatarValorPercentualParaInput = (valor) => {
    if (valor === null || valor === undefined || valor === "") {
      return formatarPorcentagem(0).formatted;
    }

    const valorTexto = String(valor);
    const valorParaMascara =
      valorTexto.includes(",") || valorTexto.includes(".")
        ? valorTexto
        : (Number(valor) * 10).toString();

    return formatarPorcentagem(valorParaMascara).formatted;
  };

  const obterPeriodoDaCompetencia = (competencia) => {
    if (!competencia) {
      return {
        dataInicialPrestacaoServico: "",
        dataFinalPrestacaoServico: "",
      };
    }

    const [ano, mes] = competencia.split("-");
    const primeiroDia = `${ano}-${mes}-01`;
    const ultimoDia = new Date(Number(ano), Number(mes), 0).getDate();

    return {
      dataInicialPrestacaoServico: primeiroDia,
      dataFinalPrestacaoServico: `${ano}-${mes}-${String(ultimoDia).padStart(2, "0")}`,
    };
  };

  const formatarCompetenciaParaInput = (data) => {
    if (!data) return "";

    const dataTexto = Array.isArray(data)
      ? `${data[0]}-${String(data[1]).padStart(2, "0")}-${String(data[2]).padStart(2, "0")}`
      : String(data);

    if (/^\d{4}-\d{2}$/.test(dataTexto)) {
      return dataTexto;
    }

    if (/^\d{4}-\d{2}-\d{2}$/.test(dataTexto)) {
      return dataTexto.slice(0, 7);
    }

    if (/^\d{2}\/\d{2}\/\d{4}$/.test(dataTexto)) {
      const [, mes, ano] = dataTexto.split("/");
      return `${ano}-${mes}`;
    }

    const dataObj = new Date(dataTexto);
    if (Number.isNaN(dataObj.getTime())) {
      return "";
    }

    const ano = dataObj.getFullYear();
    const mes = String(dataObj.getMonth() + 1).padStart(2, "0");
    return `${ano}-${mes}`;
  };

  const handleSubmit = async () => {
    if (!formData.profissional.id) {
      showError("Profissional é obrigatório");
      return;
    }

    if (!formData.competenciaPrestacaoServico) {
      showError("Mês de prestação de serviço é obrigatório");
      return;
    }

    const periodo = obterPeriodoDaCompetencia(
      formData.competenciaPrestacaoServico
    );

    setLoading(true);

    try {
      const dataToSend = {
        dataInicialPrestacaoServico: formatarData(
          periodo.dataInicialPrestacaoServico
        ),
        dataFinalPrestacaoServico: formatarData(
          periodo.dataFinalPrestacaoServico
        ),
        profissional: {
          id: formData.profissional.id,
        },
        especialidadeBonus: formData.especialidadeBonus.map((item) => ({
          especialidade: {
            id: item.especialidade.id,
          },
          valorBonus: item.valorBonus,
          valorBase: item.valorBase,
        })),
        valorAcrescimo: formData.valorAcrescimo,
        valorDesconto: formData.valorDesconto,
        valorLiquido: 0,
        pagamentoRecebido: formData.pagamentoRecebido,
        observacao: formData.observacao,
      };

      if (editingItem) {
        await folhaPagamentoService.atualizarFolhaPagamento(
          editingItem.id,
          dataToSend
        );
        showSuccess("Folha de pagamento atualizada com sucesso!");
      } else {
        await folhaPagamentoService.cadastrarFolhaPagamento(dataToSend);
        showSuccess("Folha de pagamento criada com sucesso!");
      }

      await loadFolhasPagamento();
      handleCancel();
    } catch (err) {
      showError(err.message || "Erro ao salvar folha de pagamento");
    } finally {
      setLoading(false);
    }
  };

  const handleEdit = async (item) => {
    setEditingItem(item);

    try {
      // Buscar os dados completos da folha de pagamento pelo ID
      const folhaCompleta =
        await folhaPagamentoService.listarFolhaPagamentoPorId(item.id);


      // Verificar se os valores existem antes de formatar
      const acrescimoValue = folhaCompleta.valorAcrescimo || 0;
      const descontoValue = folhaCompleta.valorDesconto || 0;
      const pagamentoRecebidoValue = folhaCompleta.pagamentoRecebido || 0;

      // Verificar se as funções de formatação existem e se os valores são válidos
      let acrescimoResult, descontoResult, pagamentoRecebidoResult;

      try {
        acrescimoResult = {
          formatted: formatarValorMoedaParaInput(acrescimoValue),
        };
        descontoResult = {
          formatted: formatarValorMoedaParaInput(descontoValue),
        };
        pagamentoRecebidoResult = {
          formatted: formatarValorMoedaParaInput(pagamentoRecebidoValue),
        };
      } catch (error) {
        console.error("Erro ao formatar valores:", error);
        acrescimoResult = { formatted: "0,00" };
        descontoResult = { formatted: "0,00" };
        pagamentoRecebidoResult = { formatted: "0,00" };
      }

      const newDisplayValues = {
        valorAcrescimo: acrescimoResult.formatted,
        valorDesconto: descontoResult.formatted,
        pagamentoRecebido: pagamentoRecebidoResult.formatted,
      };

      // Verificar se o profissional existe antes de carregar especialidades
      if (folhaCompleta.profissional?.id) {
        try {
          const profissionalCompleto =
            await profissionalService.listarProfissionalPorId(
              folhaCompleta.profissional.id
            );
          setEspecialidadesProfissional(
            profissionalCompleto.especialidade || []
          );
        } catch (err) {
          console.error(
            "Erro ao carregar especialidades do profissional:",
            err
          );
          setEspecialidadesProfissional([]);
        }
      }

      // Formatar valores das especialidades com verificação
      const newDisplayValuesEspec = {};
      if (
        folhaCompleta.especialidadeBonus &&
        Array.isArray(folhaCompleta.especialidadeBonus)
      ) {
        folhaCompleta.especialidadeBonus.forEach((esp, index) => {
          const valorBaseValue = esp.valorBase || 0;
          const valorBonusValue = esp.valorBonus || 0;

          try {
            const valorBaseResult = {
              formatted: formatarValorMoedaParaInput(valorBaseValue),
            };
            const valorBonusResult = {
              formatted: formatarValorPercentualParaInput(valorBonusValue),
            };

            newDisplayValuesEspec[`${index}_valorBase`] =
              valorBaseResult.formatted;
            newDisplayValuesEspec[`${index}_valorBonus`] =
              valorBonusResult.formatted;
          } catch (error) {
            console.error("Erro ao formatar especialidade:", error);
            newDisplayValuesEspec[`${index}_valorBase`] = "0,00";
            newDisplayValuesEspec[`${index}_valorBonus`] = "0,0";
          }
        });
      }

      setFormData({
        profissional: folhaCompleta.profissional,
        competenciaPrestacaoServico: formatarCompetenciaParaInput(
          folhaCompleta.dataInicialPrestacaoServico ||
            folhaCompleta.dataFinalPrestacaoServico
        ),
        dataInicialPrestacaoServico: folhaCompleta.dataInicialPrestacaoServico,
        dataFinalPrestacaoServico: folhaCompleta.dataFinalPrestacaoServico,
        especialidadeBonus:
          folhaCompleta.especialidadeBonus?.map((value) => ({
            id: value.id,
            especialidade: {
              id: value.especialidade?.id,
              nome: value.especialidade?.nome,
              valorPercentual: value.especialidade?.valorPercentual,
            },
            valorBase: value.valorBase || 0,
            valorBonus: value.valorBonus || 0,
          })) || [],
        valorAcrescimo: acrescimoValue,
        valorDesconto: descontoValue,
        valorLiquido: folhaCompleta.valorLiquido || 0,
        pagamentoRecebido: pagamentoRecebidoValue,
        observacao: folhaCompleta.observacao || "",
      });

      setDisplayValues(newDisplayValues);
      setDisplayValuesEspec(newDisplayValuesEspec);
      setShowForm(true);
    } catch (err) {}
  };

  const handleDelete = async (id) => {
    const confirmed = await confirmDialog({
      title: "Confirmar",
      description: "Tem certeza que deseja excluir esta folha de pagamento?",
    });

    if (!confirmed) {
      return;
    }

    setLoading(true);

    try {
      await folhaPagamentoService.deletarFolhaPagamento(id);
      showSuccess("Folha de pagamento excluída com sucesso!");
      await loadFolhasPagamento();
    } catch (err) {
      showError(err.message || "Erro ao excluir folha de pagamento");
    } finally {
      setLoading(false);
    }
  };

  const gerarPDF = async (id) => {
    setLoading(true);
    try {
      const pdfBlob = await folhaPagamentoService.gerarPdfFolhaPagamento(id);

      const url = window.URL.createObjectURL(pdfBlob);
      const link = document.createElement("a");
      link.href = url;
      link.download = `folha_pagamento_${id}.pdf`;
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);
      window.URL.revokeObjectURL(url);

      showSuccess("PDF gerado com sucesso!");
    } catch (err) {
      showError(err.message || "Erro ao gerar PDF");
    } finally {
      setLoading(false);
    }
  };

  const handleCancel = () => {
    setShowForm(false);
    setEditingItem(null);
    setEspecialidadesProfissional([]);
    setDisplayValues({
      valorAcrescimo: "",
      valorDesconto: "",
      pagamentoRecebido: "",
    });
    setDisplayValuesEspec({});
    setFormData({
      profissional: { id: "", nome: "" },
      competenciaPrestacaoServico: obterCompetenciaAtual(),
      dataInicialPrestacaoServico: "",
      dataFinalPrestacaoServico: "",
      especialidadeBonus: [],
      valorAcrescimo: 0,
      valorDesconto: 0,
      valorLiquido: 0,
      pagamentoRecebido: 0,
      observacao: "",
    });
  };

  const handleNew = () => {
    setEditingItem(null);
    setEspecialidadesProfissional([]);
    setDisplayValues({
      valorAcrescimo: "",
      valorDesconto: "",
      pagamentoRecebido: "",
    });
    setDisplayValuesEspec({});
    setFormData({
      profissional: { id: "", nome: "" },
      competenciaPrestacaoServico: obterCompetenciaAtual(),
      dataInicialPrestacaoServico: "",
      dataFinalPrestacaoServico: "",
      especialidadeBonus: [],
      valorAcrescimo: 0,
      valorDesconto: 0,
      valorLiquido: 0,
      pagamentoRecebido: 0,
      observacao: "",
    });
    setShowForm(true);
  };

  const handleKeyDown = (e) => {
    if (e.key === "Enter") {
      handleSubmit();
    }
  };

  return (
    <PageContainer className="p-6">
      <div className="mb-6">
        <h1 className="text-2xl font-bold text-gray-900 mb-2">
          Folhas de Pagamento
        </h1>
        <p className="text-gray-600">
          Gerencie as folhas de pagamento dos profissionais
        </p>
      </div>

      {/* Filtros */}
      <div className={`${cardClass} mb-6 p-4`}>
        <div className="grid grid-cols-1 md:grid-cols-5 gap-4 items-end">
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-2">
              Tipo de Pesquisa
            </label>
            <select
              name="tipoPesquisa"
              value={filtros.tipoPesquisa}
              onChange={handleFiltroChange}
              className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
            >
              <option value="todas">Todas as folhas</option>
              <option value="profissional">Por profissional</option>
              <option value="periodo">Por período</option>
              <option value="profissional_periodo">
                Por profissional e período
              </option>
            </select>
          </div>

          {(filtros.tipoPesquisa === "profissional" ||
            filtros.tipoPesquisa === "profissional_periodo") && (
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">
                Profissional
              </label>
              <select
                name="profissionalId"
                value={filtros.profissionalId}
                onChange={handleFiltroChange}
                className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
              >
                <option value="">Selecione um profissional</option>
                {profissionais.map((profissional) => (
                  <option key={profissional.id} value={profissional.id}>
                    {profissional.nome}
                  </option>
                ))}
              </select>
            </div>
          )}

          {(filtros.tipoPesquisa === "periodo" ||
            filtros.tipoPesquisa === "profissional_periodo") && (
            <>
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  Data Inicial
                </label>
                <input
                  type="date"
                  name="dataInicio"
                  value={filtros.dataInicio}
                  onChange={handleFiltroChange}
                  className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
                />
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  Data Final
                </label>
                <input
                  type="date"
                  name="dataFinal"
                  value={filtros.dataFinal}
                  onChange={handleFiltroChange}
                  className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
                />
              </div>
            </>
          )}

          <div className="flex gap-2">
            <button
              onClick={aplicarFiltrosBusca}
              disabled={loading || !isDatasValidas()}
              className={`px-4 py-2 rounded-lg flex items-center gap-2 transition-colors ${
                loading || !isDatasValidas()
                  ? "bg-gray-400 text-white cursor-not-allowed"
                  : "bg-blue-600 hover:bg-blue-700 text-white"
              }`}
            >
              <Search size={16} />
              Buscar
            </button>

            <button
              onClick={limparFiltros}
              className="bg-gray-500 hover:bg-gray-600 text-white px-4 py-2 rounded-lg flex items-center gap-2 transition-colors"
            >
              <Filter size={16} />
              Limpar
            </button>
          </div>
        </div>
      </div>

      {/* Botões */}
      <div className="mb-6 flex gap-2">
        <button
          onClick={handleNew}
          disabled={loading}
          className="bg-blue-600 hover:bg-blue-700 disabled:bg-gray-400 text-white px-4 py-2 rounded-lg flex items-center gap-2 transition-colors"
        >
          <Plus size={20} />
          Nova Folha de Pagamento
        </button>

        {selectionMode && selectedItems.length > 0 && (
          <button
            onClick={gerarPDFLote}
            disabled={loading}
            className="bg-green-600 hover:bg-green-700 disabled:bg-gray-400 text-white px-4 py-2 rounded-lg flex items-center gap-2 transition-colors"
          >
            <FileStack size={20} />
            Gerar PDF ({selectedItems.length})
          </button>
        )}
      </div>

      {/* Formulário */}
      {showForm && (
        <div className="mb-6 rounded-lg border border-gray-200 bg-white p-6 shadow-sm">
          <h3 className="text-lg font-semibold mb-4">
            {editingItem
              ? "Editar Folha de Pagamento"
              : "Nova Folha de Pagamento"}
          </h3>

          {/* Profissional e Datas */}
          <div className="grid grid-cols-1 md:grid-cols-3 gap-4 mb-4">
            <div>
              <label
                htmlFor="profissional"
                className="block text-sm font-medium text-gray-700 mb-2"
              >
                Profissional *
              </label>
              <select
                id="profissional"
                value={formData.profissional.id}
                onChange={handleProfissionalChange}
                className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-transparent"
              >
                <option value="">Selecione um profissional</option>
                {profissionais.map((profissional) => (
                  <option key={profissional.id} value={profissional.id}>
                    {profissional.nome}
                  </option>
                ))}
              </select>
            </div>

            <div>
              <label
                htmlFor="competenciaPrestacaoServico"
                className="block text-sm font-medium text-gray-700 mb-2"
              >
                Mês de Prestação de Serviço *
              </label>
              <input
                type="month"
                id="competenciaPrestacaoServico"
                name="competenciaPrestacaoServico"
                value={formData.competenciaPrestacaoServico}
                onChange={handleChange}
                onKeyDown={handleKeyDown}
                className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-transparent"
              />
              <p className="text-xs text-gray-500 mt-1">
                O período será calculado automaticamente do dia 01 ao último dia do mês.
              </p>
            </div>
          </div>

          {/* Especialidades com Valores */}
          <div className="mb-4">
            <div className="mb-4">
              <h4 className="text-md font-medium">
                Especialidades do Profissional
              </h4>
              <p className="text-sm text-gray-600">
                As especialidades são carregadas automaticamente do cadastro do
                profissional
              </p>
            </div>

            {formData.especialidadeBonus.map((item, index) => (
              <div
                key={index}
                className="grid grid-cols-4 gap-4 items-end mb-3 p-3 border rounded bg-gray-50"
              >
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">
                    Especialidade
                  </label>
                  <input
                    type="text"
                    value={item.especialidade?.nome || ""}
                    disabled
                    className="w-full px-3 py-2 border border-gray-300 rounded-md bg-gray-100 text-gray-600 cursor-not-allowed"
                  />
                </div>

                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">
                    Valor Base
                  </label>
                  <input
                    type="text"
                    value={displayValuesEspec[`${index}_valorBase`] || ""}
                    onChange={(e) =>
                      updateEspecialidadeBonus(
                        index,
                        "valorBase",
                        e.target.value
                      )
                    }
                    className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
                    placeholder="0,00"
                  />
                </div>

                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">
                    Meta Batida (%)
                  </label>
                  <input
                    type="text"
                    value={displayValuesEspec[`${index}_valorBonus`] || ""}
                    onChange={(e) =>
                      updateEspecialidadeBonus(
                        index,
                        "valorBonus",
                        e.target.value
                      )
                    }
                    className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
                    placeholder="0,0"
                  />
                </div>

                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">
                    Total
                  </label>
                  <input
                    type="text"
                    value={formatCurrencyMoeda(
                      calcularValorEspecialidade(item)
                    )}
                    disabled
                    className="w-full px-3 py-2 border border-gray-300 rounded-md bg-gray-100 text-gray-600 cursor-not-allowed font-semibold"
                  />
                </div>
              </div>
            ))}

            {!formData.profissional.id ? (
              <div className="text-center py-4 text-gray-500 border rounded-lg bg-gray-50">
                Selecione um profissional para visualizar suas especialidades.
              </div>
            ) : especialidadesProfissional.length === 0 ? (
              <div className="text-center py-4 text-yellow-600 border rounded-lg bg-yellow-50">
                Este profissional não possui especialidades cadastradas.
              </div>
            ) : formData.especialidadeBonus.length === 0 ? (
              <div className="text-center py-4 text-gray-500 border rounded-lg bg-gray-50">
                Carregando especialidades...
              </div>
            ) : null}
          </div>

          {/* Outros Valores */}
          <div className="grid grid-cols-1 md:grid-cols-4 gap-4 mb-4">
            <div>
              <label
                htmlFor="valorAcrescimo"
                className="block text-sm font-medium text-gray-700 mb-2"
              >
                Valor Acréscimo
              </label>
              <input
                type="text"
                id="valorAcrescimo"
                name="valorAcrescimo"
                value={displayValues.valorAcrescimo}
                onChange={handleChange}
                onKeyDown={handleKeyDown}
                className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                placeholder="0,00"
              />
            </div>

            <div>
              <label
                htmlFor="valorDesconto"
                className="block text-sm font-medium text-gray-700 mb-2"
              >
                Valor Desconto
              </label>
              <input
                type="text"
                id="valorDesconto"
                name="valorDesconto"
                value={displayValues.valorDesconto}
                onChange={handleChange}
                onKeyDown={handleKeyDown}
                className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                placeholder="0,00"
              />
            </div>

            <div>
              <label
                htmlFor="pagamentoRecebido"
                className="block text-sm font-medium text-gray-700 mb-2"
              >
                Relatório de Pagamento
              </label>
              <input
                type="text"
                id="pagamentoRecebido"
                name="pagamentoRecebido"
                value={displayValues.pagamentoRecebido}
                onChange={handleChange}
                onKeyDown={handleKeyDown}
                className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                placeholder="0,00"
              />
            </div>

            <div>
              <label
                htmlFor="valorLiquido"
                className="block text-sm font-medium text-gray-700 mb-2"
              >
                Valor Líquido
              </label>
              <input
                type="text"
                id="valorLiquido"
                name="valorLiquido"
                value={formatCurrencyMoeda(formData.valorLiquido)}
                readOnly
                className="w-full px-3 py-2 border border-gray-300 rounded-md bg-gray-100 text-gray-600 focus:outline-none"
                placeholder="R$ 0,00"
              />
              <p className="text-xs text-gray-500 mt-1">
                Calculado automaticamente
              </p>
            </div>
          </div>

          {/* Campo Observação */}
          <div className="mb-4">
            <label
              htmlFor="observacao"
              className="block text-sm font-medium text-gray-700 mb-2"
            >
              Observação
            </label>
            <textarea
              id="observacao"
              name="observacao"
              value={formData.observacao}
              onChange={handleChange}
              rows={3}
              className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-transparent"
              placeholder="Digite observações sobre esta folha de pagamento (opcional)"
            />
          </div>

          {/* Resumo do Cálculo */}
          <div className="mb-4 p-4 bg-blue-50 rounded-lg border border-blue-200">
            <h4 className="text-sm font-medium text-blue-900 mb-2">
              Resumo do Cálculo
            </h4>
            <div className="text-sm text-blue-800 space-y-1">
              <div className="flex justify-between">
                <span>Total das Especialidades:</span>
                <span>
                  {formatCurrencyMoeda(
                    formData.especialidadeBonus.reduce(
                      (sum, item) => sum + calcularValorEspecialidade(item),
                      0
                    )
                  )}
                </span>
              </div>
              <div className="flex justify-between">
                <span>+ Acréscimo:</span>
                <span>{formatCurrencyMoeda(formData.valorAcrescimo)}</span>
              </div>
              <div className="flex justify-between">
                <span>- Desconto:</span>
                <span>{formatCurrencyMoeda(formData.valorDesconto)}</span>
              </div>
              <hr className="border-blue-300" />
              <div className="flex justify-between font-semibold">
                <span>= Valor Líquido:</span>
                <span>{formatCurrencyMoeda(formData.valorLiquido)}</span>
              </div>
            </div>
          </div>

          <div className="flex gap-2">
            <button
              onClick={handleSubmit}
              disabled={
                loading ||
                !formData.profissional.id ||
                !formData.competenciaPrestacaoServico
              }
              className="bg-green-600 hover:bg-green-700 disabled:bg-gray-400 text-white px-4 py-2 rounded-lg flex items-center gap-2 transition-colors"
            >
              <Save size={16} />
              {loading ? "Salvando..." : "Salvar"}
            </button>

            <button
              onClick={handleCancel}
              className="bg-gray-500 hover:bg-gray-600 text-white px-4 py-2 rounded-lg flex items-center gap-2 transition-colors"
            >
              <X size={16} />
              Cancelar
            </button>
          </div>
        </div>
      )}

      {/* Lista */}
      <div className="bg-white rounded-lg shadow border">
        <div className="p-4 border-b">
          <div className="flex justify-between items-center">
            <h3 className="text-lg font-semibold">
              Lista de Folhas de Pagamento
            </h3>
            <div className="flex items-center gap-4">
              {selectionMode && selectedItems.length > 0 && (
                <div className="text-sm text-gray-600">
                  {selectedItems.length} de {folhasPaginadas.length}{" "}
                  selecionados
                </div>
              )}
              <span className="text-sm text-gray-500">
                {totalRegistros} registro(s) encontrado(s)
              </span>
              {folhasFiltradas.length > 0 && (
                <button
                  onClick={toggleSelectionMode}
                  className={`px-3 py-1 rounded-lg text-sm flex items-center gap-2 transition-colors ${
                    selectionMode
                      ? "bg-orange-100 text-orange-700 hover:bg-orange-200"
                      : "bg-blue-100 text-blue-700 hover:bg-blue-200"
                  }`}
                >
                  {selectionMode ? (
                    <>
                      <X size={16} />
                      Cancelar Seleção
                    </>
                  ) : (
                    <>
                      <CheckSquare size={16} />
                      Selecionar
                    </>
                  )}
                </button>
              )}
            </div>
          </div>
        </div>

        {loading && folhasFiltradas.length === 0 ? (
          <div className="p-8 text-center text-gray-500">
            Carregando folhas de pagamento...
          </div>
        ) : folhasFiltradas.length === 0 ? (
          <div className="p-8 text-center text-gray-500">
            {filtros.profissionalId || filtros.dataInicio || filtros.dataFinal
              ? "Nenhum registro foi localizado com os filtros aplicados"
              : "Nenhuma folha de pagamento cadastrada"}
          </div>
        ) : (
          <>
            <div>
              <table className="w-full">
                <thead className="bg-gray-50">
                  <tr>
                    {selectionMode && (
                      <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                        <input
                          type="checkbox"
                          checked={
                            selectedItems.length === folhasPaginadas.length
                          }
                          onChange={handleSelectAll}
                          className="rounded border-gray-300 text-blue-600 focus:ring-blue-500"
                        />
                      </th>
                    )}
                    <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                      Profissional
                    </th>
                    <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                      Período do Serviço
                    </th>
                    <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                      Especialidades
                    </th>
                    <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                      Valor Líquido
                    </th>
                    <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                      Data Lançamento
                    </th>
                    <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                      Observação
                    </th>
                    <th className="px-6 py-3 text-right text-xs font-medium text-gray-500 uppercase tracking-wider">
                      Ações
                    </th>
                  </tr>
                </thead>
                <tbody className="divide-y divide-gray-200">
                  {folhasPaginadas.map((item) => (
                    <tr key={item.id} className="hover:bg-gray-50">
                      {selectionMode && (
                        <td className="px-6 py-4 whitespace-nowrap">
                          <input
                            type="checkbox"
                            checked={selectedItems.includes(item.id)}
                            onChange={() => handleSelectItem(item.id)}
                            className="rounded border-gray-300 text-blue-600 focus:ring-blue-500"
                          />
                        </td>
                      )}
                      <td className="px-6 py-4 whitespace-nowrap">
                        <div className="flex items-center">
                          <div className="flex-shrink-0 h-8 w-8">
                            <div className="h-8 w-8 rounded-full bg-blue-100 flex items-center justify-center">
                              <User className="h-4 w-4 text-blue-600" />
                            </div>
                          </div>
                          <div className="ml-3">
                            <div className="text-sm font-medium text-gray-900">
                              {item.profissional?.nome || "N/A"}
                            </div>
                          </div>
                        </div>
                      </td>
                      <td className="px-6 py-4 whitespace-nowrap">
                        <div className="text-sm text-gray-900">
                          <div className="flex items-center gap-1">
                            <Calendar className="h-4 w-4 text-gray-400" />
                            <span>
                              {formatarData(item.dataInicialPrestacaoServico)}{" "}
                              {" - "}
                              {formatarData(item.dataFinalPrestacaoServico)}
                            </span>
                          </div>
                        </div>
                      </td>
                      <td className="px-6 py-4">
                        <div className="text-sm text-gray-900">
                          {item.especialidadeBonus &&
                          item.especialidadeBonus.length > 0 ? (
                            <div className="space-y-1">
                              {item.especialidadeBonus.map((esp, index) => {
                                const total = calcularValorTotalEspecialidade(esp);
                                return (
                                  <div
                                    key={index}
                                    className="border-b pb-1 mb-1 last:border-b-0"
                                  >
                                    <div className="flex justify-between items-center">
                                      <span className="text-xs bg-blue-100 text-blue-800 px-2 py-1 rounded">
                                        {esp.especialidade?.nome || "N/A"}
                                      </span>
                                      <span className="text-xs font-bold text-green-600">
                                        {formatCurrencyMoeda(total)}
                                      </span>
                                    </div>
                                  </div>
                                );
                              })}
                            </div>
                          ) : (
                            <span className="text-gray-400 text-xs">
                              Nenhuma especialidade
                            </span>
                          )}
                        </div>
                      </td>
                      <td className="px-6 py-4 whitespace-nowrap">
                        <div className="text-sm font-bold text-green-600">
                          {formatCurrencyMoeda(item.valorLiquido)}
                        </div>
                      </td>
                      <td className="px-6 py-4 whitespace-nowrap">
                        <div className="text-sm text-gray-900">
                          {formatarData(item.dataLancamentoFolhaPagamento)}
                        </div>
                      </td>
                      <td className="px-6 py-4 whitespace-nowrap text-center">
                        {item.observacao && item.observacao.trim() ? (
                          <button
                            onClick={() => mostrarObservacao(item.observacao)}
                            className="text-blue-600 hover:text-blue-900 p-1 rounded transition-colors inline-flex items-center gap-1"
                            title="Ver observação"
                          >
                            <Eye size={14} />
                          </button>
                        ) : (
                          <span className="text-gray-400 text-xs">
                            Sem observação
                          </span>
                        )}
                      </td>
                      <td className="px-6 py-4 whitespace-nowrap text-right text-sm font-medium">
                        <div className="flex justify-end gap-2">
                          <button
                            onClick={() => gerarPDF(item.id)}
                            disabled={loading}
                            className="text-green-600 hover:text-green-900 disabled:text-gray-400 p-1 rounded transition-colors"
                            title="Gerar PDF"
                          >
                            <Download size={16} />
                          </button>

                          <button
                            onClick={() => handleEdit(item)}
                            disabled={loading}
                            className="text-blue-600 hover:text-blue-900 disabled:text-gray-400 p-1 rounded transition-colors"
                            title="Editar"
                          >
                            <Edit2 size={16} />
                          </button>

                          <button
                            onClick={() => handleDelete(item.id)}
                            disabled={loading}
                            className="text-red-600 hover:text-red-900 disabled:text-gray-400 p-1 rounded transition-colors"
                            title="Excluir"
                          >
                            <Trash2 size={16} />
                          </button>
                        </div>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>

            {/* Controles de Paginação */}
            {totalPaginas > 1 && (
              <div className="px-6 py-4 border-t border-gray-200">
                <div className="flex items-center justify-between">
                  <div className="text-sm text-gray-500">
                    Mostrando {indiceInicial + 1} até{" "}
                    {Math.min(indiceFinal, totalRegistros)} de {totalRegistros}{" "}
                    registros
                  </div>

                  <div className="flex items-center space-x-2">
                    {/* Primeira página */}
                    <button
                      onClick={irParaPrimeiraPagina}
                      disabled={paginacao.paginaAtual === 1}
                      className="p-2 rounded-md border border-gray-300 bg-white text-gray-500 hover:bg-gray-50 disabled:opacity-50 disabled:cursor-not-allowed"
                      title="Primeira página"
                    >
                      <ChevronsLeft size={16} />
                    </button>

                    {/* Página anterior */}
                    <button
                      onClick={irParaPaginaAnterior}
                      disabled={paginacao.paginaAtual === 1}
                      className="p-2 rounded-md border border-gray-300 bg-white text-gray-500 hover:bg-gray-50 disabled:opacity-50 disabled:cursor-not-allowed"
                      title="Página anterior"
                    >
                      <ChevronLeft size={16} />
                    </button>

                    {/* Números das páginas */}
                    <div className="flex space-x-1">
                      {gerarNumerosPaginas().map((numeroPagina) => (
                        <button
                          key={numeroPagina}
                          onClick={() => irParaPagina(numeroPagina)}
                          className={`px-3 py-2 rounded-md text-sm font-medium ${
                            paginacao.paginaAtual === numeroPagina
                              ? "bg-blue-600 text-white"
                              : "bg-white text-gray-500 border border-gray-300 hover:bg-gray-50"
                          }`}
                        >
                          {numeroPagina}
                        </button>
                      ))}
                    </div>

                    {/* Próxima página */}
                    <button
                      onClick={irParaProximaPagina}
                      disabled={paginacao.paginaAtual === totalPaginas}
                      className="p-2 rounded-md border border-gray-300 bg-white text-gray-500 hover:bg-gray-50 disabled:opacity-50 disabled:cursor-not-allowed"
                      title="Próxima página"
                    >
                      <ChevronRight size={16} />
                    </button>

                    {/* Última página */}
                    <button
                      onClick={irParaUltimaPagina}
                      disabled={paginacao.paginaAtual === totalPaginas}
                      className="p-2 rounded-md border border-gray-300 bg-white text-gray-500 hover:bg-gray-50 disabled:opacity-50 disabled:cursor-not-allowed"
                      title="Última página"
                    >
                      <ChevronsRight size={16} />
                    </button>
                  </div>
                </div>
              </div>
            )}
          </>
        )}
      </div>

      {/* Modal de Observação */}
      {showObservacao && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center p-4 z-50">
          <div className="bg-white rounded-lg max-w-2xl w-full max-h-[80vh]">
            <div className="p-6">
              <div className="flex justify-between items-center mb-4">
                <h3 className="text-lg font-semibold flex items-center gap-2">
                  <MessageSquare size={20} />
                  Observação
                </h3>
                <button
                  onClick={() => setShowObservacao(false)}
                  className="text-gray-500 hover:text-gray-700"
                >
                  <X size={20} />
                </button>
              </div>

              <div className="bg-gray-50 p-4 rounded-lg border">
                <p className="text-gray-700 whitespace-pre-wrap">
                  {observacaoView}
                </p>
              </div>

              <div className="flex justify-end mt-4">
                <button
                  onClick={() => setShowObservacao(false)}
                  className="bg-gray-500 hover:bg-gray-600 text-white px-4 py-2 rounded-lg transition-colors"
                >
                  Fechar
                </button>
              </div>
            </div>
          </div>
        </div>
      )}
    </PageContainer>
  );
};

export default FolhaPagamento;
